// src/components/tenant/TenantShell.tsx — the portal frame for a resident or an applicant: the phone app's look on the web.
//
// One navigation: on a laptop a clean top bar (the logo, the sections as pills, Ask Lotus, the bell, the avatar menu);
// below lg the bar keeps the logo and the icons and the sections move to the app's floating glass tab bar at the foot.
// No sidebar, no hamburger, no tab strip. The `tenant` class it puts on <html> switches index.css to the cream / gold
// tokens; staff never mount this, so their screens are untouched. The portal is always light (dark is dropped here).
//
// A new section is one entry in RESIDENT_TABS; a page under one (Pay rent under Rent) names it with `also`. Profile and
// Notifications are reached from the avatar menu and the bell (the bell opens /app/notifications).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, ReceiptText, Wrench, FileText, MessagesSquare, Building2, KeyRound, Search, Sparkles, LogOut, Globe, ChevronDown, Bell, UserRound, type LucideIcon } from 'lucide-react';
import { useAuth } from '../../stores/auth';
import { messagesApi } from '../../lib/comms';
import { useHeaderSlot } from '../../lib/headerSlot';
import { holdAppReady, introPending, clearIntro } from '../../lib/appReady';
import { prefersReducedMotion } from '../../lib/lottie';
import { AssistantDrawer } from '../AssistantDrawer';
import { NOTIFICATIONS_KEY, notificationsApi } from '../../lib/portal';
import { PageActions } from '../ui/menu';
import { BrandIntro } from '../BrandLoader';

export interface TenantTab { key: string; label: string; path: string; Icon: LucideIcon; /** Leaves the app for the public site. */ site?: boolean; /** Pages under this section. */ also?: string[] }

/** A resident's sections — the bottom bar, the top bar and the phone app use the same six. */
const RESIDENT_TABS: TenantTab[] = [
  { key: 'home', label: 'Home', path: '/app/my-home', Icon: Home, also: ['/app/my-home/spaces'] },
  { key: 'rent', label: 'Rent', path: '/app/my-home/rent', Icon: ReceiptText, also: ['/app/my-home/pay'] },
  { key: 'repairs', label: 'Repairs', path: '/app/my-home/repairs', Icon: Wrench },
  { key: 'documents', label: 'Documents', path: '/app/my-home/documents', Icon: FileText },
  { key: 'messages', label: 'Messages', path: '/app/messages', Icon: MessagesSquare },
  { key: 'building', label: 'Building', path: '/app/my-home/building', Icon: Building2 },
];

/** An applicant's sections: their booking, and the homes on the website. */
const APPLICANT_TABS: TenantTab[] = [
  { key: 'booking', label: 'My booking', path: '/app/my-booking', Icon: KeyRound },
  { key: 'homes', label: 'Find a home', path: '/rent', Icon: Search, site: true },
];

const isActive = (t: TenantTab, path: string) => (t.key === 'home' ? path === t.path || path === `${t.path}/` : path.startsWith(t.path)) || !!t.also?.some((p) => path.startsWith(p));

export function TenantShell({ kind }: { kind: 'resident' | 'applicant' }) {
  const { user, can, canAccessPage, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slot = useHeaderSlot();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const tabs = kind === 'resident' ? RESIDENT_TABS : APPLICANT_TABS;
  const canAsk = can('assistant.use');

  // The tenant palette, always light, while the portal is on screen.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.add('tenant');
    html.classList.remove('dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    const was = meta?.getAttribute('content');
    meta?.setAttribute('content', '#FBF8F3');
    return () => { html.classList.remove('tenant'); if (was) meta?.setAttribute('content', was); };
  }, []);
  // The boot loader waits for the first paint; the brand intro plays once after signing in (as in the staff shell).
  useEffect(() => {
    const release = holdAppReady();
    const frame = requestAnimationFrame(() => release());
    return () => { cancelAnimationFrame(frame); release(); };
  }, []);
  const [intro, setIntro] = useState(() => introPending() && !prefersReducedMotion());
  useEffect(() => { clearIntro(); }, []);
  // ⌘K / Ctrl+K opens Ask Lotus; Escape closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && canAsk) { e.preventDefault(); setAssistantOpen(true); }
      if (e.key === 'Escape') setAssistantOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canAsk]);
  // Each section opens at the top.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);

  const unread = useQuery({ queryKey: ['/messages/unread'], queryFn: messagesApi.unread, enabled: canAccessPage('messages'), refetchInterval: 60_000 }).data?.unread ?? 0;
  const badge = (t: TenantTab) => (t.key === 'messages' ? unread : 0);
  // A page that is not a section (change password, the calendar) keeps the title it declares with <PageHeader>.
  const inSection = tabs.some((t) => !t.site && isActive(t, pathname)) || /^\/app\/(profile|notifications)/.test(pathname);
  const homePath = tabs[0]!.path;

  return (
    <div data-app className="min-h-dvh">
      <header className="t-bar t-glass safe-top sticky top-0 z-30 border-b" style={{ borderColor: 'var(--t-hairline)' }}>
        <div className="mx-auto flex h-[64px] max-w-[1120px] items-center gap-2 px-4 lg:h-[72px] lg:px-8">
          <Link to={homePath} aria-label="Lotus Living — your home" className="flex shrink-0 items-center rounded-full py-2 pr-2">
            <img src="/logo.svg" alt="Lotus Living" className="h-8 w-auto lg:h-9" draggable={false} />
          </Link>

          <nav aria-label="Your portal" className="mx-auto hidden items-center gap-1 lg:flex">
            {tabs.map((t) => {
              const on = !t.site && isActive(t, pathname);
              return (
                <Link key={t.key} to={t.path} aria-current={on ? 'page' : undefined}
                  className={`relative inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition ${on ? 'bg-bronze-50 text-bronze-700' : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900'}`}>
                  <t.Icon className="h-[17px] w-[17px]" strokeWidth={on ? 2.2 : 1.8} />
                  {t.label}
                  {badge(t) > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-bronze-500 px-1.5 text-[11px] font-semibold leading-none text-on-primary">{badge(t)}</span>}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1 lg:ml-0">
            {canAsk && (
              <button type="button" onClick={() => setAssistantOpen(true)} aria-label="Ask Lotus" title="Ask Lotus (⌘K)"
                className="grid h-11 w-11 place-items-center rounded-full text-bronze-600 transition hover:bg-bronze-50">
                <Sparkles className="h-[19px] w-[19px]" />
              </button>
            )}
            <TenantBell on={pathname.startsWith('/app/notifications')} />
            <AvatarMenu name={[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || ''} email={user?.email ?? ''}
              onSignOut={async () => { await logout(); navigate('/login'); }} />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1120px] px-4 pb-36 pt-5 lg:px-8 lg:pb-20 lg:pt-10">
        {!inSection && slot?.title && (
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <h1 className="t-title">{slot.title}</h1>
            {(slot.actions || slot.menu) && <div className="flex items-center gap-2">{slot.actions}{slot.menu && <PageActions items={slot.menu} />}</div>}
          </div>
        )}
        <Outlet />
      </main>

      {/* The app's floating glass tab bar, below lg. */}
      <nav aria-label="Your portal" className="fixed inset-x-0 bottom-0 z-30 px-4 lg:hidden" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
        <div className={`t-glass mx-auto flex h-[62px] items-center gap-1 rounded-full border border-white/90 px-2 ${tabs.length > 2 ? 'max-w-md' : 'max-w-[240px]'}`} style={{ boxShadow: 'var(--shadow-float)' }}>
          {tabs.map((t) => {
            const on = !t.site && isActive(t, pathname);
            return (
              <NavLink key={t.key} to={t.path} aria-label={t.label} title={t.label} aria-current={on ? 'page' : undefined}
                className={`relative grid h-[50px] min-w-[44px] flex-1 place-items-center rounded-full transition active:scale-95 ${on ? 'bg-bronze-50 text-bronze-600' : 'text-ink-500 hover:text-ink-800'}`}>
                <t.Icon className="h-[23px] w-[23px]" strokeWidth={on ? 2.2 : 1.6} />
                {badge(t) > 0 && <span className="absolute right-[18%] top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-bronze-500 px-1 text-[10px] font-semibold leading-none text-on-primary ring-2 ring-white">{badge(t)}</span>}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      {intro && <BrandIntro onDone={() => setIntro(false)} />}
    </div>
  );
}

/** The bell: how many notifications are unread; it opens the Notifications page. */
function TenantBell({ on }: { on: boolean }) {
  const unread = useQuery({ queryKey: NOTIFICATIONS_KEY, queryFn: notificationsApi.list, refetchInterval: 60_000, refetchIntervalInBackground: false }).data?.unread ?? 0;
  return (
    <Link to="/app/notifications" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} title={unread ? `${unread} unread` : 'Notifications'} aria-current={on ? 'page' : undefined}
      className={`relative grid h-11 w-11 place-items-center rounded-full transition ${on ? 'bg-bronze-50 text-bronze-700' : 'text-ink-700 hover:bg-ink-50'}`}>
      <Bell className="h-[19px] w-[19px]" strokeWidth={on ? 2.2 : 1.8} />
      {unread > 0 && <span className="absolute right-1 top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-bronze-500 px-1 text-[10px] font-semibold leading-none text-on-primary ring-2 ring-white">{unread > 99 ? '99+' : unread}</span>}
    </Link>
  );
}

/** The avatar and its menu: who is signed in, the profile, notifications, change password, the website, sign out. */
function AvatarMenu({ name, email, onSignOut }: { name: string; email: string; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const initials = name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  const item = 'flex min-h-[44px] w-full items-center gap-3 px-4 text-left text-[14px] font-medium text-ink-800 transition hover:bg-ink-50';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} aria-label="Your account"
        className="flex h-11 items-center gap-1 rounded-full pl-1 pr-1 transition hover:bg-ink-50 lg:pr-2">
        <span className="grid h-9 w-9 place-items-center rounded-full text-[13px] font-semibold text-on-primary" style={{ background: 'var(--t-foil)' }}>{initials}</span>
        <ChevronDown className="hidden h-4 w-4 text-ink-500 lg:block" />
      </button>
      {open && (
        <div role="menu" className="menu-pop absolute right-0 top-full z-50 mt-2 w-[260px] overflow-hidden rounded-[20px] border bg-white py-1.5" style={{ borderColor: 'var(--t-hairline)', boxShadow: 'var(--shadow-pop)' }}>
          <div className="border-b px-4 pb-3 pt-2" style={{ borderColor: 'var(--t-hairline)' }}>
            <div className="truncate text-[15px] font-semibold text-ink-900">{name}</div>
            <div className="truncate text-[12.5px] text-ink-500">{email}</div>
          </div>
          <div className="py-1">
            <Link to="/app/profile" role="menuitem" onClick={() => setOpen(false)} className={item}><UserRound className="h-4 w-4 text-bronze-600" />Profile</Link>
            <Link to="/app/notifications" role="menuitem" onClick={() => setOpen(false)} className={item}><Bell className="h-4 w-4 text-bronze-600" />Notifications</Link>
            <Link to="/app/profile#password" role="menuitem" onClick={() => setOpen(false)} className={item}><KeyRound className="h-4 w-4 text-bronze-600" />Change password</Link>
            <Link to="/" role="menuitem" onClick={() => setOpen(false)} className={item}><Globe className="h-4 w-4 text-bronze-600" />Lotus Living website</Link>
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onSignOut(); }} className={item}><LogOut className="h-4 w-4 text-bronze-600" />Sign out</button>
          </div>
        </div>
      )}
    </div>
  );
}
